import { useState, useEffect } from 'react';
import axios from 'axios';
import { Heart, Bookmark } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SavedPostsPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSavedPosts();
  }, []);

  const fetchSavedPosts = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/posts/saved`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load saved posts');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (postId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(
        `${API}/posts/${postId}/save`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosts((prev) => prev.filter((post) => post.id !== postId));
      toast.success('Post removed from saved');
    } catch (error) {
      toast.error('Failed to unsave post');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-2">ॐ</div>
          <p className="text-slate-600">Loading saved posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <div className="text-center mb-8 peaceful-enter">
        <h2 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
          Saved Posts
        </h2>
        <p className="text-slate-600">Your collection of spiritual content</p>
      </div>

      {posts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl">
          <Bookmark className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">No saved posts yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {posts.map((post, index) => (
            <div
              key={post.id}
              data-testid={`saved-post-${index}`}
              className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6 peaceful-enter"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {/* User Info */}
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={post.user_avatar}
                  alt={post.user_name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-orange-100"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-800">{post.user_name}</h3>
                    {post.user_role === 'pandit' && (
                      <span className="px-2 py-0.5 bg-[#FF9933] text-white text-xs rounded-full">Verified</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">{new Date(post.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Content */}
              <div className="mb-4">
                {post.post_type === 'mantra' && (
                  <div className="bg-orange-50/50 rounded-xl p-6 mb-4 border border-orange-100">
                    <p className="text-lg text-slate-800 text-center mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                      {post.content}
                    </p>
                    {post.mantra_meaning && (
                      <p className="text-sm text-slate-600 text-center italic">Meaning: {post.mantra_meaning}</p>
                    )}
                  </div>
                )}

                {post.post_type === 'text' && (
                  <p className="text-slate-700 leading-relaxed">{post.content}</p>
                )}

                {post.post_type === 'video' && post.media_url && (
                  <div className="mb-3">
                    <p className="text-slate-700 leading-relaxed mb-3">{post.content}</p>
                    <div className="aspect-video rounded-xl overflow-hidden bg-slate-100">
                      <iframe
                        src={post.media_url.replace('watch?v=', 'embed/')}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                <div className="flex items-center gap-2 text-slate-600">
                  <Heart className="w-5 h-5" />
                  <span className="text-sm font-medium">{post.likes_count} Namaste</span>
                </div>
                <button
                  data-testid={`unsave-btn-${index}`}
                  onClick={() => handleUnsave(post.id)}
                  className="flex items-center gap-2 text-[#FF9933] hover:text-[#E68A00] transition-colors"
                >
                  <Bookmark className="w-5 h-5" fill="#FF9933" />
                  <span className="text-sm font-medium">Saved</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
